/**
 * Cache Control Service
 * Resolves VAPT point: Cache Control Implementation
 * Implements comprehensive caching policies and security headers
 */

class CacheControlService {
  constructor() {
    // Cache control configurations for different resource types
    this.cacheConfigs = {
      // Sensitive data - never cache
      sensitive: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, private, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      
      // API responses - no cache for dynamic data
      api: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, private',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      
      // Static assets - cache with validation
      static: {
        'Cache-Control': 'public, max-age=300, must-revalidate', // 5 minutes
        'ETag': true
      },
      
      // Images - longer cache with validation
      images: {
        'Cache-Control': 'public, max-age=3600, must-revalidate', // 1 hour
        'ETag': true
      },
      
      // Authentication pages - never cache
      auth: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, private, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    };

    // Sensitive paths that should never be cached
    this.sensitivePaths = new Set([
      '/api/',
      '/auth/',
      '/login',
      '/logout',
      '/profile',
      '/dashboard',
      '/forms/',
      '/reports/',
      '/admin/'
    ]);

    this.isInitialized = false;
  }

  /**
   * Initialize cache control for the application
   */
  initialize() {
    if (this.isInitialized) return;

    // Set up fetch interceptor for API calls
    this.setupFetchInterceptor();
    
    // Set up page-level cache controls
    this.setupPageCacheControl();
    
    // Set up session storage cache management
    this.setupSessionCacheManagement();
    
    // Set up browser history cache control
    this.setupHistoryCacheControl();
    
    this.isInitialized = true;

  }

  /**
   * Set up fetch interceptor to add cache control headers
   */
  setupFetchInterceptor() {
    // Store original fetch
    const originalFetch = window.fetch;
    
    window.fetch = async (url, options = {}) => {
      // Apply cache control headers to requests
      const enhancedOptions = this.enhanceRequestWithCacheControl(url, options);
      
      try {
        const response = await originalFetch(url, enhancedOptions);
        
        // Process response headers for cache control
        this.processResponseCacheControl(response, url);
        
        return response;
      } catch (error) {
        console.error('Fetch error:', error);
        throw error;
      }
    };
  }

  /**
   * Enhance request with appropriate cache control headers
   */
  enhanceRequestWithCacheControl(url, options) {
    // Skip adding custom headers to prevent CORS issues
    return options;
  }

  /**
   * Process response cache control headers
   */
  processResponseCacheControl(response, url) {
    // Check if response has proper cache control headers
    const cacheControl = response.headers.get('Cache-Control');
    
    if (this.isSensitivePath(url) && (!cacheControl || !cacheControl.includes('no-store'))) {
      console.warn(`Sensitive path ${url} may not have proper cache control headers`);
    }

    // Log cache control for debugging
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Cache control for ${url}:`, {
        cacheControl,
        pragma: response.headers.get('Pragma'),
        expires: response.headers.get('Expires')
      });
    }
  }

  /**
   * Set up page-level cache control
   */
  setupPageCacheControl() {
    // Don't run on login page to prevent flickering
    if (window.location.pathname === '/login') {
      return;
    }
    
    // Set meta tags for page cache control
    this.setPageMetaTags();
    
    // Handle browser back/forward navigation (only refresh if coming from cache)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this.isCurrentPageSensitive()) {
        // Only refresh if page was actually cached and we're on a sensitive page
        // Add delay to prevent immediate refresh after login
        setTimeout(() => {
          if (this.isCurrentPageSensitive()) {
            window.location.reload();
          }
        }, 100);
      }
    });

    // Handle visibility change (reduce frequency to prevent flickering)
    let visibilityTimeout;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Debounce visibility changes to prevent excessive checks
        clearTimeout(visibilityTimeout);
        visibilityTimeout = setTimeout(() => {
          this.validatePageFreshness();
        }, 1000); // 1 second delay
      }
    });
  }

  /**
   * Set meta tags for cache control
   */
  setPageMetaTags() {
    // Remove existing cache control meta tags
    const existingTags = document.querySelectorAll('meta[http-equiv*="cache"], meta[http-equiv*="Cache"], meta[http-equiv*="pragma"], meta[http-equiv*="expires"]');
    existingTags.forEach(tag => tag.remove());

    // Add comprehensive cache control meta tags
    const metaTags = [
      { httpEquiv: 'Cache-Control', content: 'no-cache, no-store, must-revalidate, private, max-age=0' },
      { httpEquiv: 'Pragma', content: 'no-cache' },
      { httpEquiv: 'Expires', content: '0' }
    ];

    metaTags.forEach(({ httpEquiv, content }) => {
      const meta = document.createElement('meta');
      meta.setAttribute('http-equiv', httpEquiv);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    });
  }

  /**
   * Set up session storage cache management
   */
  setupSessionCacheManagement() {
    // Clear sensitive data from storage on page unload
    window.addEventListener('beforeunload', () => {
      this.clearSensitiveCache();
    });

    // Set up periodic cache cleanup
    setInterval(() => {
      this.cleanupExpiredCache();
    }, 300000); // 5 minutes
  }

  /**
   * Set up browser history cache control
   */
  setupHistoryCacheControl() {
    // Override history methods to manage cache
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(state, title, url) {
      // Don't clear cache for navigation - this causes flickering
      // Just apply cache headers without refreshing
      return originalPushState.apply(window.history, arguments);
    };

    window.history.replaceState = function(state, title, url) {
      // Don't clear cache for navigation - this causes flickering
      // Just apply cache headers without refreshing  
      return originalReplaceState.apply(window.history, arguments);
    };
  }

  /**
   * Determine cache policy for a given URL
   */
  determineCachePolicy(url) {
    if (this.isSensitivePath(url)) {
      return this.cacheConfigs.sensitive;
    }
    
    if (url.includes('/api/')) {
      return this.cacheConfigs.api;
    }
    
    if (this.isImageUrl(url)) {
      return this.cacheConfigs.images;
    }
    
    if (this.isStaticAsset(url)) {
      return this.cacheConfigs.static;
    }
    
    // Default to no-cache for unknown resources
    return this.cacheConfigs.sensitive;
  }

  /**
   * Check if path is sensitive
   */
  isSensitivePath(url) {
    if (!url) return true; // Default to sensitive for safety
    
    const path = typeof url === 'string' ? url : url.pathname || '';
    
    return Array.from(this.sensitivePaths).some(sensitivePath => 
      path.includes(sensitivePath)
    );
  }

  /**
   * Check if current page is sensitive
   */
  isCurrentPageSensitive() {
    return this.isSensitivePath(window.location.pathname);
  }

  /**
   * Check if URL is an image
   */
  isImageUrl(url) {
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i;
    return imageExtensions.test(url);
  }

  /**
   * Check if URL is a static asset
   */
  isStaticAsset(url) {
    const staticExtensions = /\.(css|js|woff|woff2|ttf|eot)$/i;
    return staticExtensions.test(url) || url.includes('/static/');
  }

  /**
   * Clear sensitive data from cache
   */
  clearSensitiveCache() {
    try {
      // Clear session storage of sensitive data
      const sensitiveKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (this.isSensitiveStorageKey(key)) {
          sensitiveKeys.push(key);
        }
      }
      sensitiveKeys.forEach(key => sessionStorage.removeItem(key));

      // Clear local storage of sensitive data
      const sensitiveLSKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (this.isSensitiveStorageKey(key)) {
          sensitiveLSKeys.push(key);
        }
      }
      sensitiveLSKeys.forEach(key => localStorage.removeItem(key));

    } catch (error) {
      console.error('Error clearing sensitive cache:', error);
    }
  }

  /**
   * Check if storage key contains sensitive data
   */
  isSensitiveStorageKey(key) {
    if (!key) return false;
    
    const sensitiveKeywords = [
      'token', 'auth', 'session', 'user', 'password', 'credential',
      'api', 'form', 'data', 'personal', 'profile'
    ];
    
    return sensitiveKeywords.some(keyword => 
      key.toLowerCase().includes(keyword)
    );
  }

  /**
   * Clear page cache
   */
  clearPageCache() {
    // Don't force immediate refresh - this causes flickering
    // Instead, just clear the browser cache programmatically
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    // Set no-cache headers for future requests
    this.setPageMetaTags();
  }

  /**
   * Clean up expired cache entries
   */
  cleanupExpiredCache() {
    try {
      // Check for cache timestamps and clean expired entries
      const now = Date.now();
      const maxAge = 300000; // 5 minutes

      // Clean session storage
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('cache_')) {
          try {
            const data = JSON.parse(sessionStorage.getItem(key));
            if (data.timestamp && (now - data.timestamp) > maxAge) {
              sessionStorage.removeItem(key);
            }
          } catch (e) {
            // Invalid JSON, remove it
            sessionStorage.removeItem(key);
          }
        }
      }
    } catch (error) {
      console.error('Error cleaning up cache:', error);
    }
  }

  /**
   * Validate page freshness
   */
  validatePageFreshness() {
    // Don't validate login page to prevent refresh loops
    if (window.location.pathname === '/login') {
      return;
    }
    
    // Only validate on very old pages to prevent frequent refreshes
    if (this.isCurrentPageSensitive()) {
      const pageLoadTime = window.performance?.timing?.navigationStart || Date.now();
      const maxPageAge = 7200000; // 2 hours - very conservative to prevent flickering
      
      if (Date.now() - pageLoadTime > maxPageAge) {
        console.warn('Page is very stale, refreshing silently');
        // Silent refresh without user intervention
        window.location.reload();
      }
    }
  }

  /**
   * Set cache control for specific element/resource
   */
  setCacheControl(element, cacheType = 'sensitive') {
    const config = this.cacheConfigs[cacheType] || this.cacheConfigs.sensitive;
    
    if (element && element.setAttribute) {
      // Set cache control attributes for elements that support it
      Object.entries(config).forEach(([key, value]) => {
        if (typeof value === 'string') {
          element.setAttribute(`data-${key.toLowerCase()}`, value);
        }
      });
    }
  }

  /**
   * Force clear all caches
   */
  forceCleanAllCache() {
    // Clear browser cache programmatically (limited capability)
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Clear storage
    sessionStorage.clear();
    this.clearSensitiveCache();

    // Set aggressive no-cache headers
    this.setPageMetaTags();
  }

  /**
   * Get cache status report
   */
  getCacheStatus() {
    return {
      initialized: this.isInitialized,
      currentPageSensitive: this.isCurrentPageSensitive(),
      sessionStorageKeys: sessionStorage.length,
      localStorageKeys: localStorage.length,
      cacheConfigs: Object.keys(this.cacheConfigs),
      sensitivePaths: Array.from(this.sensitivePaths)
    };
  }
}

// Create and export singleton instance
const cacheControlService = new CacheControlService();

// Make it globally available for debugging
window.cacheControlService = cacheControlService;

export default cacheControlService;